import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { linkProjectsToSOW, getLinkedProjectsBySOWId } from "../../http/requests/sow";
import { getProjectAdminDetails } from "../../http/requests/project-admin";
import { getSOWData } from "../../redux/actions";
import { toastOptions } from "../../common/utils/toasterOptions";

const projectId = (p) => p?.projectId ?? p?.ProjectId;
const projectName = (p) => p?.projectName ?? p?.ProjectName ?? "";
const clientName = (p) => p?.clientName ?? p?.ClientName ?? "";
const projectLabel = (p) => `${projectName(p)} (${clientName(p)})`;

const LinkProjectsModal = ({ open, onClose, sow, onSuccess }) => {
  const dispatch = useDispatch();
  const sowListClientFilterId = useSelector((s) => s.MODULES.sowListClientFilterId);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedProjects([]);
    const loadProjects = async () => {
      try {
        const res = await getProjectAdminDetails(
          1,
          500,
          "ASC",
          "projectName",
          "",
          false
        );
        const data = res?.data ?? res?.Data ?? [];
        setProjects(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load projects:", err);
        toast.error("Failed to load projects", toastOptions);
      }
    };
    loadProjects();
  }, [open]);

  useEffect(() => {
    if (!open || !sow || projects.length === 0) return;
    const sowId = sow?.sowId ?? sow?.SOWId;
    if (!sowId) return;
    const loadLinkedProjects = async () => {
      setLoadingLinked(true);
      try {
        const ids = await getLinkedProjectsBySOWId(sowId);
        const idList = Array.isArray(ids) ? ids : [];
        const linked = idList
          .map((id) => projects.find((p) => projectId(p) === id))
          .filter(Boolean);
        setSelectedProjects(linked);
      } catch (err) {
        console.error("Failed to load linked projects:", err);
      } finally {
        setLoadingLinked(false);
      }
    };
    loadLinkedProjects();
  }, [open, sow, projects]);

  const handleClose = () => {
    setSelectedProjects([]);
    onClose();
  };

  const handleLink = async () => {
    const sowId = sow?.sowId ?? sow?.SOWId;
    if (!sowId) {
      toast.error("Invalid SOW", toastOptions);
      return;
    }
    const ids = [...new Set(selectedProjects.map((p) => projectId(p)).filter(Boolean))];
    if (ids.length === 0) {
      toast.error("Select at least one project", toastOptions);
      return;
    }
    try {
      setSubmitting(true);
      await linkProjectsToSOW(sowId, ids);
      toast.success("Projects linked successfully", toastOptions);
      dispatch(
        getSOWData({
          pageNo: 1,
          pageSize: 10,
          sortBy: "sowName",
          sortDir: "ASC",
          searchData: "",
          clientId: sowListClientFilterId,
        })
      );
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.data?.errorMessage ||
          err?.message ||
          "Failed to link projects",
        toastOptions
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sowName = sow?.sowName ?? sow?.SOWName ?? "";
  const startDate = sow?.sowStartDate ?? sow?.SOWStartDate;
  const endDate = sow?.sowEndDate ?? sow?.SOWEndDate;
  const formatDate = (d) => {
    if (!d) return "";
    const date = typeof d === "string" ? d : d?.split?.("T")?.[0] ?? "";
    return date;
  };

  const filterOptions = (options, { inputValue }) => {
    const q = (inputValue || "").toLowerCase().trim();
    if (!q) return options;
    return options.filter(
      (p) =>
        (projectName(p) || "").toLowerCase().includes(q) ||
        (clientName(p) || "").toLowerCase().includes(q)
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          minWidth: 500,
          maxWidth: "min(864px, calc(100vw - 22rem))",
          margin: 0,
          maxHeight: "calc(100vh - 48px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          left: "calc(50% + 10rem)",
          top: "50%",
          transform: "translate(-50%, -50%)",
        },
      }}
    >
      <DialogTitle sx={{ color: "#1773bc", fontWeight: 600, pb: 0 }}>
        Link Projects to SOW
      </DialogTitle>
      <Divider sx={{ borderColor: "#1773bc", opacity: 0.5 }} />
      <DialogContent sx={{ pt: 1.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: "0.875rem", color: "text.secondary", minWidth: 90 }}
            >
              SOW Name:
            </Box>
            <Box component="span" sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
              {sowName || "—"}
            </Box>
            <Box
              component="span"
              sx={{ fontSize: "0.875rem", color: "text.secondary", ml: 2, minWidth: 72 }}
            >
              Start Date:
            </Box>
            <Box component="span" sx={{ fontSize: "0.875rem" }}>
              {formatDate(startDate) || "—"}
            </Box>
            <Box
              component="span"
              sx={{ fontSize: "0.875rem", color: "text.secondary", ml: 1, minWidth: 68 }}
            >
              End Date:
            </Box>
            <Box component="span" sx={{ fontSize: "0.875rem" }}>
              {formatDate(endDate) || "—"}
            </Box>
          </Box>
          <Autocomplete
            multiple
            size="small"
            options={projects}
            value={selectedProjects}
            onChange={(_, newValue) => setSelectedProjects(newValue)}
            getOptionLabel={(p) => projectLabel(p)}
            isOptionEqualToValue={(opt, val) => projectId(opt) === projectId(val)}
            filterOptions={filterOptions}
            loading={loadingLinked}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Projects"
                placeholder="Search by project name or client name..."
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={projectId(option)}
                  label={projectLabel(option)}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderOption={(props, option) => (
              <li {...props} key={projectId(option)}>
                {projectLabel(option)}
              </li>
            )}
            sx={{ mt: 0.5 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#1773bc" }}
          disabled={submitting || selectedProjects.length === 0}
          onClick={handleLink}
        >
          {submitting ? "Linking..." : "Link Projects"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkProjectsModal;
